/* eslint-disable camelcase */
import {Component, createRef} from 'react';
import PropTypes from 'prop-types';
import APIUtils from '@livongo/utils/api';
import StorageUtils from '@livongo/utils/storage';
import Arg from '@livongo/arg';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import format from 'date-fns/format';
import isUndefined from 'lodash/isUndefined';
import {StringValue} from 'google-protobuf/google/protobuf/wrappers_pb';
import {Empty} from 'google-protobuf/google/protobuf/empty_pb';
import {Program as ProgramType} from '@livongo/protobuf-grpc-external-enrollment/lib/livongo/protobuf/common/user/program_pb';
import {
    GetCourseSummaryRequest,
    GetDesiredCourseSummaryRequest,
    GetEducationServiceInfoRequest,
    GetLessonRequest,
    RenderingPromiseClient,
    GetEducationSupportLinksRequest,
    SupportLinks as SupportLinksRequest,
    UpdateAnswersRequest,
    UpdatePageStatusRequest,
    UserActivityPromiseClient,
    UserManagerPromiseClient,
    UserAnswers,
} from '@livongo/protobuf-grpc-external-education';
import {withTranslation} from 'react-i18next';
import {
    PageTypes,
    FieldTypes,
    normalizePageData,
    getDomainName,
} from 'utils/page';
import {ErrorTypes} from 'utils/error-types';
import {notifyWebViewLessonTitle} from 'utils/web-view';
import Footer from 'ui/Footer';
import Loader from 'ui/Loader';
import Modal from 'ui/Modal';
import Navigation from 'navigation/Navigation';
import Landing from 'landing/Landing';
import PageContainer from 'lesson/PageContainer';
import Outro from 'outro/Outro';
import IntroVideo from 'intro-video/IntroVideo';
import {ACCESS_TOKEN_COOKIE} from './config';
import 'sass/main.scss';

const enableDevTools = window.__GRPCWEB_DEVTOOLS__ || (() => {}); // eslint-disable-line
const isDev =
    (window?.process?.env?.NODE_ENV ?? process.env.NODE_ENV) ===
        'development' ||
    (window?.process?.env?.TGPS_ENV ?? process.env.TGPS_ENV) === 'development';
const serviceUrl = window?.process?.env?.SERVICE_URL ?? process.env.SERVICE_URL;
const accessCodeCookie = 'education-access-code';
const accessTokenMP = 'access_token';
const refreshTokenMP = 'refresh_token';
const educationToken = 'education-token';
const memberProgram = 'member_program';
const defaultProgram = 'PREDIABETES';
// Status codes defined here: https://grpc.github.io/grpc/core/md_doc_statuscodes.html.  There didn't seem to be a good way to import the codes directly.
const grpcUnauthenticatedError = 16;
const cookieDomain = getDomainName(); // This variable is temporary until iOS is done with foodlogger 2.1 (September)

const initialState = {
    isLoading: true,
    hasStarted: false,
    hasFinished: false,
    showErrorModal: false,
    errorType: null,
    currentPage: null,
    highestPageViewed: -1,
    previousAnswers: null,
    storedUserAnswers: {},
    direction: null,
    isSelectedAnswerCorrect: false,
    isNextDisabled: false,
};

class App extends Component {
    static propTypes = {
        t: PropTypes.func.isRequired,
        selectedLang: PropTypes.string,
    };

    // TODO: This should be removed after integration test
    state = {
        ...initialState,
    };
    _renderingClient = new RenderingPromiseClient(serviceUrl);
    _userActivityClient = new UserActivityPromiseClient(serviceUrl);
    _userManagerClient = new UserManagerPromiseClient(serviceUrl); // Used to generate an accessCode in development only
    _curriculumId = 1; // TODO: get this from user data or URL?
    _courseId = null;
    _lessonId = null;
    _curriculumCdcId = null;
    _userId = null;
    _publicId = null;
    _courseResponse = null;
    _lessonResponse = null;
    _supportLinksResponse = null;
    _pages = null;
    _isOneApp = null;
    _hasUsedScale = null;
    _pageContainer = createRef();
    _requestMetadata = this.props.selectedLang
        ? {
              'Accept-Language': this.props.selectedLang,
          }
        : null;

    constructor(props) {
        super(props);

        // enable debugging grpc calls
        enableDevTools([this._renderingClient, this._userActivityClient]);
    }

    componentDidMount() {
        // Prevent caching on go back (button)
        window.onpageshow = event => {
            if (event.persisted) {
                window.location.reload();
            }
        };

        // Run APIs
        this._loadData();
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        const {currentPage} = this.state;
        const {currentPage: prevCurrentPage} = prevState;

        if (
            (!prevCurrentPage && currentPage) ||
            (prevCurrentPage &&
                currentPage &&
                prevCurrentPage.getPageId() !== currentPage.getPageId() &&
                currentPage.getPageType() !== PageTypes.ACKNOWLEDGE)
        ) {
            // a new page was navigated to, mark it as a viewed  page on the BE
            this._markPageViewed(currentPage);
        }

        if (prevProps.selectedLang !== this.props.selectedLang) {
            this._requestMetadata = {
                ...this._requestMetadata,
                'Accept-Language': this.props.selectedLang,
            };
        }
    }

    _onRefreshClick = () => {
        this._loadData();

        this.setState({
            showErrorModal: false,
            errorType: null,
        });
    };

    _onStartClick = async lessonId => {
        if (this._lessonId !== lessonId) {
            this._lessonId = lessonId;

            this.setState({...initialState});

            await this._loadLesson();

            this.setState({
                isLoading: false,
            });
        }

        const lessonUserData = this._lessonResponse.getLessonUserData();
        const resumeFromId = lessonUserData.getResumePageId();
        const initialPage = this._findPageById(resumeFromId);
        const previousAnswers = this._findPreviousAnswers(initialPage);

        this.setState(prevState => {
            const {getPageNumber} = initialPage;
            const {highestPageViewed} = prevState;

            return {
                hasStarted: true,
                currentPage: initialPage,
                highestPageViewed:
                    getPageNumber() > highestPageViewed
                        ? getPageNumber()
                        : highestPageViewed,
                previousAnswers,
            };
        });

        notifyWebViewLessonTitle(
            this._currentLesson
                .getTextFieldsMap()
                .get(FieldTypes.TITLE)
                .getTextValue()
        );
    };

    _onTOCPageSelected = pageNumber => {
        const currentPageNumber = this.state.currentPage.getPageNumber();
        const newPage = this._findPageByNumber(pageNumber);
        const previousAnswers = this._findPreviousAnswers(newPage);

        this.setState({
            currentPage: newPage,
            direction:
                newPage.getPageNumber() > currentPageNumber ? 'right' : 'left',
            isNextDisabled: false,
            previousAnswers,
        });
    };

    _onBackClick = () => {
        if (this.state.currentPage.getPageNumber() === 1) {
            return;
        }

        this.setState((prevState, props) => {
            const {currentPage} = prevState;
            let previousPageNumber = currentPage.getPageNumber() - 1;
            let newPage = this._findPageByNumber(previousPageNumber);

            if (newPage.getPageType() === PageTypes.ACKNOWLEDGE) {
                // the new page would have been an acknowledge page, skip it since they aren't supposed to show on back click
                previousPageNumber--;
                newPage = this._findPageByNumber(previousPageNumber);
            }

            const previousAnswers = this._findPreviousAnswers(newPage);

            return {
                hasStarted: previousPageNumber > 0,
                currentPage: newPage,
                direction: 'left',
                isNextDisabled: false,
                previousAnswers,
            };
        });
    };

    _onNextClick = async () => {
        const {currentPage, isNextDisabled} = this.state;
        let newAnswer = {};
        let isAnswerCorrect = false;

        // avoid swipe left when next is disabled
        if (isNextDisabled) {
            return;
        }

        if (currentPage.getPageType() === PageTypes.QUESTION_PAGE) {
            // we're clicking next on a question page, first check if the form is valid
            const answerData = this._pageContainer.current.formRef.submit();

            if (!answerData) {
                // the form did not pass validation, the UI will disallow it from moving forward so just get out of here
                return;
            } else {
                // form validated, submit answer to BE
                const answersRequest = new UpdateAnswersRequest();
                const answer = new UserAnswers();
                const {
                    answer_cms_id: cmsId,
                    answer_text: answerInput,
                } = answerData;

                newAnswer = {
                    [currentPage.getPageId()]: {
                        selectedAnswerCmsIdsList:
                            cmsId !== 'other' ? [cmsId] : [],
                        selectedOtherAnswerText: isEmpty(answerInput)
                            ? null
                            : {value: answerInput},
                    },
                };

                if (answerInput) {
                    const answerText = new StringValue();

                    answerText.setValue(answerInput);

                    answer.setSelectedOtherAnswerText(answerText);
                } else {
                    const selectedAnswer = currentPage
                        .getAnswersList()
                        .find(item => item.getAnswerCmsId() === cmsId);

                    isAnswerCorrect = selectedAnswer
                        ? selectedAnswer.getIsCorrect()
                        : false;

                    answer.setSelectedAnswerCmsIdsList([cmsId]);
                }

                answersRequest.setCourseId(this._courseId);
                answersRequest.setPageId(currentPage.getPageId());
                answersRequest.setUserAnswers(answer);

                try {
                    await this._userActivityClient.updateAnswers(
                        answersRequest,
                        this._requestMetadata
                    );
                } catch (error) {
                    console.log('Answers Error:', error); // eslint-disable-line no-console

                    this.setState({
                        showErrorModal: true,
                        errorType: ErrorTypes.RESPONSE,
                    });
                }
            }
        }

        this.setState((prevState, props) => {
            const {
                currentPage: prevCurrentPage,
                storedUserAnswers: prevStoredUserAnswers,
            } = prevState;
            const nextPageNumber = prevCurrentPage.getPageNumber() + 1;

            if (nextPageNumber > this._pages.length) {
                return {
                    hasStarted: false,
                    hasFinished: true,
                    storedUserAnswers: {
                        ...prevStoredUserAnswers,
                        ...newAnswer,
                    },
                };
            } else {
                const nextPage = this._findPageByNumber(nextPageNumber);
                const previousAnswers = this._findPreviousAnswers(nextPage);

                return {
                    currentPage: nextPage,
                    direction: 'right',
                    previousAnswers,
                    storedUserAnswers: {
                        ...prevStoredUserAnswers,
                        ...newAnswer,
                    },
                    isSelectedAnswerCorrect: isAnswerCorrect,
                };
            }
        });
    };

    _onExitClick = () => {
        this._onGoToLanding();
    };

    _onGoToLanding = async () => {
        this.setState(initialState);

        await this._loadCourse();
        await this._loadLesson();

        this.setState({
            isLoading: false,
        });
    };

    _onSetNextDisabled = isNextDisabled => {
        this.setState({
            isNextDisabled,
        });
    };

    _onIntroVideoError() {
        this.setState({
            showErrorModal: true,
            errorType: ErrorTypes.INTRO_VIDEO,
        });
    }

    _getGrpcProgram() {
        const storedProgram = StorageUtils.get({
            key: memberProgram,
            type: 'cookie',
        });
        const program = storedProgram || defaultProgram;
        const programParam = ProgramType.Values[program];

        return programParam;
    }

    _isGLP1FeatureEnabled() {
        // This is the feature flag for 1.3.x vs 1.4.x on the education gRPC calls
        return Boolean(
            StorageUtils.get({
                key: memberProgram,
                type: 'cookie',
            })
        );
    }

    _markPageViewed(page) {
        const pageRequest = new UpdatePageStatusRequest();

        if (this._isGLP1FeatureEnabled()) {
            pageRequest.setProgram(this._getGrpcProgram());
        }

        pageRequest.setCourseId(this._courseId);
        pageRequest.setPageId(page.getPageId());

        this._userActivityClient.updatePageStatus(
            pageRequest,
            this._requestMetadata
        );

        this.setState(prevState => {
            const highestPageViewed =
                page.getPageNumber() > prevState.highestPageViewed
                    ? page.getPageNumber()
                    : prevState.highestPageViewed;

            return {highestPageViewed};
        });
    }

    _findPageByNumber(pageNumber) {
        return this._pages.find(page => {
            return page.getPageNumber() === pageNumber;
        });
    }

    _findPageById(pageId) {
        return this._pages.find(page => {
            return page.getPageId() === pageId;
        });
    }

    _findPreviousAnswers(page) {
        if (page.getPageType() !== PageTypes.QUESTION_PAGE) {
            return null;
        }

        const {storedUserAnswers} = this.state;
        const pageId = page.getPageId();
        const answerData = this._lessonResponse
            .getLessonUserData()
            .getPageUserDataMap()
            .get(pageId);

        if (!isUndefined(storedUserAnswers[pageId])) {
            return storedUserAnswers[pageId];
        } else {
            return answerData.hasUserAnswers()
                ? answerData.getUserAnswers().toObject()
                : {};
        }
    }

    _handleGrpcError(message, error) {
        console.log(message, error); // eslint-disable-line no-console

        if (error.code === grpcUnauthenticatedError) {
            window.open(
                this._isOneApp
                    ? `${
                          window?.process?.env?.ONEAPP_URL ??
                          process.env.ONEAPP_URL
                      }/condition-management/home?redirectToEducation=true`
                    : `${
                          window?.process?.env?.MEMBER_PORTAL_URL ??
                          process.env.MEMBER_PORTAL_URL
                      }?redirectToEducation=true`,
                '_self'
            );
        } else {
            this.setState({
                showErrorModal: true,
                errorType: ErrorTypes.LESSON,
            });
        }
    }

    async _loadData() {
        this.setState(initialState);
        const oneappEducationUrl =
            window.location.hostname ===
                window?.process?.env?.ONEAPP_EDUCATION_URL ??
            process.env.ONEAPP_EDUCATION_URL;

        this._isOneApp =
            oneappEducationUrl ||
            window.location.hostname.includes('teladoc') ||
            Arg('oneapp');

        if (!this._isOneApp) {
            document.title = 'Livongo';
        }

        await this._loadJwtToken();
        await this._loadCourse();
        await this._loadLesson();
        await this._getWeight();
        await this._loadSupportLinks();

        // START DEBUG
        // The below two lines are for debug only to jump to a specific page. Uncomment them and use with the START DEBUG portion below in the setState call.
        // const currentPage = this._findPageByNumber(23);
        // const previousAnswers = this._findPreviousAnswers(currentPage);
        // END DEBUG

        this.setState({
            isLoading: false,
            // START DEBUG
            // The below three properties are for debug only, use in conjunction with setting the currentPage and previousAnswers above otherwise they should remain commented out.
            // hasStarted: true,
            // currentPage,
            // previousAnswers,
            // END DEBUG
        });
    }

    async _loadJwtToken() {
        const {
            manualTokenExchange,
            accessCode,
            source,
            introVideo,
            locale,
        } = Arg.all();
        const previousAccessCode = StorageUtils.get({
            key: accessCodeCookie,
            type: 'cookie',
        });
        let accessToken = StorageUtils.get({
            key: ACCESS_TOKEN_COOKIE,
            type: 'cookie',
        });
        let code;

        try {
            // This is block of code is a redirect link for iOS to food 2.1 feature
            // This block is temporary until iOS is done with foodlogger 2.1 (September)
            const forFoodInsights = source && source.includes('food_insights');
            const hasNewAccessCode =
                accessCode && accessCode !== previousAccessCode;

            if (forFoodInsights && !hasNewAccessCode) {
                const lastCookie = StorageUtils.get({
                    key: educationToken,
                    type: 'cookie',
                    useNative: true,
                });

                if (lastCookie) {
                    const urlRedirect = `${
                        window?.process?.env?.MEMBER_PORTAL_URL ??
                        process.env.MEMBER_PORTAL_URL
                    }?source=${source}&locale=${locale}`.replace(
                        'http:',
                        'https:'
                    );

                    // eslint-disable-next-line require-atomic-updates
                    window.location.href = urlRedirect;
                }
            }

            // `${baseURL}?manualTokenExchange=true`) enables testing the app in prodcution mode through a browser
            // change (isDev || manualTokenExchange) to (false) to enable local testing with a specific user
            if (!forFoodInsights && (isDev || manualTokenExchange)) {
                const metadata = {
                    Authorization: `Bearer ${
                        window?.process?.env?.SERVICE_JWT_TOKEN ??
                        process.env.SERVICE_JWT_TOKEN
                    }`,
                };

                let serviceResponse;

                if (this._isGLP1FeatureEnabled()) {
                    const serviceInfoRequest = new GetEducationServiceInfoRequest();

                    serviceInfoRequest.setProgram(this._getGrpcProgram());

                    serviceResponse = await this._userManagerClient.getServiceInfo(
                        serviceInfoRequest,
                        metadata
                    );
                } else {
                    serviceResponse = await this._userManagerClient.getServiceInfo(
                        new Empty(),
                        metadata
                    );
                }
                const args = Arg.parse(serviceResponse.getWebUrl());

                code = args.accessCode;
            } else {
                code = accessCode;
            }

            if (forFoodInsights) {
                // This is block of code is a redirect link for iOS to food 2.1 feature
                // This block is temporary until iOS is done with foodlogger 2.1 (September)
                this._isOneApp = source === 'oneapp_food_insights';
            }

            if (code && code !== previousAccessCode) {
                const {data} = await APIUtils.post(
                    this._isOneApp
                        ? `${
                              window?.process?.env?.SERVICE_URL ??
                              process.env.SERVICE_URL
                          }/service-education/v1/users/me/education/auth`
                        : '/v1/users/me/auth',
                    {
                        code,
                        grant_type: 'authorization_code', // eslint-disable-line camelcase
                    },
                    {
                        headers: {
                            Authorization: `Basic ${
                                source && source === 'member-portal'
                                    ? window?.process?.env?.WEB_AUTH_SECRET ??
                                      process.env.WEB_AUTH_SECRET
                                    : window?.process?.env
                                          ?.MOBILE_AUTH_SECRET ??
                                      process.env.MOBILE_AUTH_SECRET
                            }`,
                        },
                    }
                );
                const {access_token: token} = data;

                accessToken = token;

                StorageUtils.set({
                    key: accessCodeCookie,
                    value: code,
                    type: 'cookie',
                });
                StorageUtils.set({
                    key: ACCESS_TOKEN_COOKIE,
                    value: accessToken,
                    type: 'cookie',
                });

                // This is block of code is a redirect link for iOS to food 2.1 feature
                // This block is temporary until iOS is done with foodlogger 2.1 (September)
                // Remove the cookies so that we can make fresh ones.
                if (forFoodInsights) {
                    StorageUtils.remove({
                        key: accessTokenMP,
                        type: 'cookie',
                        useNative: true,
                    });
                    StorageUtils.remove({
                        key: refreshTokenMP,
                        type: 'cookie',
                        useNative: true,
                    });
                }

                // This is block of code is a redirect link for iOS to food 2.1 feature
                // This block is temporary until iOS is done with foodlogger 2.1 (September)
                if (forFoodInsights) {
                    StorageUtils.set({
                        key: educationToken,
                        value: data,
                        type: 'cookie',
                        useNative: true,
                        cookieOptions: {
                            domain: cookieDomain,
                        },
                        stringify: true,
                    });

                    const urlRedirect = `${
                        window?.process?.env?.MEMBER_PORTAL_URL ??
                        process.env.MEMBER_PORTAL_URL
                    }?source=${source}&locale=${locale}`.replace(
                        'http:',
                        'https:'
                    );

                    // eslint-disable-next-line require-atomic-updates
                    window.location.href = urlRedirect;
                }
            }

            this._requestMetadata = {
                ...this._requestMetadata,
                Authorization: `Bearer ${accessToken}`,
            };

            if (!introVideo) {
                const [
                    userInfo,
                    {
                        data: {publicUUID},
                    },
                ] = await Promise.all([
                    this._userManagerClient.getUserInfo(
                        new Empty(),
                        this._requestMetadata
                    ),
                    APIUtils.get('/v1/users/me/partners/PUBLIC', null, {
                        headers: this._requestMetadata,
                    }),
                ]);

                this._userId = userInfo.getUserUuid();
                this._publicId = publicUUID;
            }
        } catch (error) {
            this._handleGrpcError('JWT Error:', error);
        }
    }

    _toUTC(string) {
        const date = new Date(string);

        return date.getTime() + date.getTimezoneOffset() * 60 * 1000;
    }

    _getReadings({start, end, count, isIrregular = null, isUTC = false}) {
        const FORMAT_DATETIME_NO_TZ = "yyyy-MM-dd'T'HH:mm:ss";

        return APIUtils.get(
            'v1/users/me/reading/weights',
            {
                ...(start && {
                    start: format(
                        isUTC ? this._toUTC(start) : new Date(start),
                        FORMAT_DATETIME_NO_TZ
                    ),
                }),
                ...(end && {
                    end: format(
                        isUTC ? this._toUTC(end) : new Date(end),
                        FORMAT_DATETIME_NO_TZ
                    ),
                }),
                ...(count && {count}),
                ...(!isNil(isIrregular) && {isIrregular}),
            },
            {
                headers: this._requestMetadata,
            }
        ).then(response => response.data.weightReadings);
    }

    async _getWeight() {
        try {
            const data = await this._getReadings({
                start: new Date(0),
                count: 1,
            });

            this._hasUsedScale = !isEmpty(data);
        } catch (err) {
            this._hasUsedScale = true; // don't stop user from taking the lesson if the api failed.
        }
    }

    async _loadCourse() {
        // here we'll check to see if a program is passed in via a URL param.
        // if it is, we'll set it as a cookie value.
        // then we'll check to see if that cookie is there, if it is - we'll use that value to make the request --
        // if not, we'll default to prediabetes (legacy DPP)
        const {program} = Arg.all();

        if (program && ProgramType.Values[program]) {
            StorageUtils.set({
                key: memberProgram,
                value: program,
                type: 'cookie',
            });
        } else {
            // We are removing here to make sure that when someone turn the feature flag off, the cookies get unset
            StorageUtils.remove({
                key: memberProgram,
                value: program,
                type: 'cookie',
            });
        }

        let courseRequest;

        if (this._isGLP1FeatureEnabled()) {
            courseRequest = new GetDesiredCourseSummaryRequest();

            courseRequest.setProgram(this._getGrpcProgram());
        } else {
            courseRequest = new GetCourseSummaryRequest();

            courseRequest.setCurriculumId(this._curriculumId);
        }

        const timezone = new StringValue();

        timezone.setValue(Intl.DateTimeFormat().resolvedOptions().timeZone);
        courseRequest.setTimeZone(timezone);

        try {
            if (this._isGLP1FeatureEnabled()) {
                this._courseResponse = await this._renderingClient.getDesiredCourseSummary(
                    courseRequest,
                    this._requestMetadata
                );
            } else {
                this._courseResponse = await this._renderingClient.getCourseSummary(
                    courseRequest,
                    this._requestMetadata
                );
            }

            if (isDev) {
                console.log('COURSE DATA:', this._courseResponse.toObject()); // eslint-disable-line no-console
            }

            this._courseId = this._courseResponse
                .getCourseUserData()
                .getCourseId();
            this._lessonId = this._courseResponse
                .getCourseUserData()
                .getResumeLessonId();
            this._curriculumCdcId = this._courseResponse.getCurriculumPartNumber();
        } catch (error) {
            this._handleGrpcError('Course Error:', error);
        }
    }

    async _loadLesson() {
        const lessonRequest = new GetLessonRequest();

        lessonRequest.setCourseId(this._courseId);
        lessonRequest.setLessonId(this._lessonId);

        if (this._isGLP1FeatureEnabled()) {
            lessonRequest.setProgram(this._getGrpcProgram());
        }

        try {
            this._lessonResponse = await this._renderingClient.getLesson(
                lessonRequest,
                this._requestMetadata
            );

            if (isDev) {
                console.log('LESSON DATA:', this._lessonResponse.toObject()); // eslint-disable-line no-console
            }

            this._lessonResponse.setPagesList(
                normalizePageData(
                    this._lessonResponse.getPagesList(),
                    this.props.t
                )
            );

            this._pages = this._lessonResponse.getPagesList();
            this._currentLesson = this._courseResponse
                .getLessonInfosList()
                .find(lesson => {
                    return lesson.getLessonId() === this._lessonId;
                });
        } catch (error) {
            this._handleGrpcError('Lesson Error:', error);
        }
    }

    async _loadSupportLinks() {
        try {
            if (this._isGLP1FeatureEnabled()) {
                const supportLinksRequest = new GetEducationSupportLinksRequest();

                supportLinksRequest.setProgram(this._getGrpcProgram());
                this._supportLinksResponse = await this._renderingClient.getEducationSupportLinks(
                    supportLinksRequest,
                    this._requestMetadata
                );
            } else {
                const supportLinksRequest = new SupportLinksRequest();

                this._supportLinksResponse = await this._renderingClient.getSupportLinks(
                    supportLinksRequest,
                    this._requestMetadata
                );
            }
        } catch (error) {
            this._handleGrpcError('Links Error:', error);
        }
    }

    _renderPage() {
        const {hasFinished} = this.state;
        const lessonNumber = this._currentLesson.getLessonNumber();
        const {manualTokenExchange = false} = Arg.all();

        if (hasFinished) {
            return (
                <Outro
                    isOneApp={this._isOneApp}
                    userId={this._userId}
                    publicId={this._publicId}
                    lessonId={this._lessonId}
                    lessonNumber={lessonNumber}
                    supportLinks={this._supportLinksResponse}
                    onGoToLanding={this._onGoToLanding}
                />
            );
        }

        return (
            <Landing
                isOneApp={this._isOneApp}
                lessonNumber={lessonNumber}
                course={this._courseResponse}
                currentLesson={this._lessonResponse}
                onStartClick={this._onStartClick}
                hasUsedScale={this._hasUsedScale}
                isManualTokenExchange={manualTokenExchange}
                program={this._getGrpcProgram()}
            />
        );
    }

    render() {
        const {introVideo: showIntroVideo} = Arg.all();
        const {
            isLoading,
            hasStarted,
            hasFinished,
            showErrorModal,
            errorType,
            currentPage,
            highestPageViewed,
            previousAnswers,
            direction,
            isSelectedAnswerCorrect,
            isNextDisabled,
        } = this.state;

        return (
            <div className="App" role="main">
                {isLoading ? (
                    <Loader />
                ) : showErrorModal ? (
                    <Modal onClose={this._onRefreshClick} type={errorType} />
                ) : showIntroVideo ? (
                    <IntroVideo
                        isOneApp={this._isOneApp}
                        requestMetaData={this._requestMetadata}
                        onError={this._onIntroVideoError}
                        userActivityClient={this._userActivityClient}
                    />
                ) : (
                    <>
                        <Navigation
                            isOneApp={this._isOneApp}
                            hasStarted={hasStarted}
                            hasFinished={hasFinished}
                            currentLesson={this._lessonResponse}
                            currentPage={currentPage}
                            highestPageViewed={highestPageViewed}
                            isNextDisabled={isNextDisabled}
                            onBackClick={this._onBackClick}
                            onNextClick={this._onNextClick}
                            onExitClick={this._onExitClick}
                            onTOCPageSelected={this._onTOCPageSelected}
                        >
                            <div className="App-content">
                                {hasStarted ? (
                                    <PageContainer
                                        ref={this._pageContainer}
                                        pages={this._pages}
                                        currentPage={currentPage}
                                        previousAnswers={previousAnswers}
                                        direction={direction}
                                        isSelectedAnswerCorrect={
                                            isSelectedAnswerCorrect
                                        }
                                        isNextDisabled={isNextDisabled}
                                        onSetNextDisabled={
                                            this._onSetNextDisabled
                                        }
                                        onSwipedLeft={this._onNextClick}
                                        onSwipedRight={this._onBackClick}
                                    />
                                ) : (
                                    this._renderPage()
                                )}
                            </div>
                        </Navigation>
                        {!hasStarted && !hasFinished && (
                            <Footer
                                isOneApp={this._isOneApp}
                                curriculumCdcId={this._curriculumCdcId}
                            />
                        )}
                    </>
                )}
            </div>
        );
    }
}

export default withTranslation()(App);
